import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, ShieldCheck, CreditCard, Zap, CheckCircle, Lock } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ContactForm from "@/components/ContactForm";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const heroVisualInitial = { opacity: 0, y: 20 };
const heroVisualAnimate = { opacity: 1, y: 0 };
const heroVisualTransition = { duration: 0.5, delay: 0.3 };

const PLATFORMS = [
  { name: "WooCommerce", slug: "woocommerce", desc: "The most popular WordPress e-commerce plugin." },
  { name: "Shopify", slug: "shopify", desc: "Fast, simple hosted e-commerce." },
  { name: "Magento 2", slug: "magento", desc: "Enterprise-grade open-source e-commerce." },
  { name: "PrestaShop", slug: "prestashop", desc: "Lightweight and open-source." },
  { name: "BigCommerce", slug: "bigcommerce", desc: "Built for high-growth merchants." },
  { name: "OpenCart", slug: "opencart", desc: "Free, fast and flexible." },
  { name: "Drupal", slug: "drupal", desc: "For agencies needing full stack control." },
];

function PlatformCard({ p }) {
  return (
    <Link
      to={`/integrations/${p.slug}`}
      className="group flex items-center gap-4 bg-white border border-brand-light-grey rounded-2xl p-5 hover:border-brand-aqua hover:shadow-lg transition-all"
      data-testid={`merchant-platform-${p.slug}`}
    >
      <div className="w-11 h-11 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0 group-hover:bg-brand-aqua transition-colors">
        <Globe size={20} className="text-brand-aqua group-hover:text-white transition-colors" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold text-brand-black group-hover:text-brand-aqua transition-colors">{p.name}</p>
        <p className="text-xs text-brand-grey">{p.desc}</p>
      </div>
      <ArrowRight size={16} className="text-brand-grey group-hover:text-brand-aqua flex-shrink-0 transition-colors" />
    </Link>
  );
}

export default function MerchantIntegrationsPage() {
  return (
    <div>
      <SEOHead title="E-Commerce Payment Integrations" description="Accept online payments on WooCommerce, Shopify, Magento 2, PrestaShop, BigCommerce, OpenCart and more. Plug-and-play setup, clear pricing, real support." path="/integrations" />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">E-Commerce Payments</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                Start accepting payments on <span className="gradient-text">your online store</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
                Plug-and-play payment integrations for WooCommerce, Shopify, Magento and more. Clear pricing, next-day settlement, and a real person to call when you need help.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/contact?product=E-Commerce%20Payments&category=integrations&source=merchant-integrations" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-all btn-shine" data-testid="merchant-int-cta">Get integration pricing <ArrowRight size={16} /></Link>
                <Link to="/quote/online" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors">Estimate my costs</Link>
              </motion.div>
            </motion.div>
            <motion.div initial={heroVisualInitial} animate={heroVisualAnimate} transition={heroVisualTransition} className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-xl border border-brand-light-grey/60 p-5 max-w-sm mx-auto">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-brand-green" />
                  <span className="text-xs font-semibold text-brand-grey">Payment Gateway: Live</span>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "WooCommerce", status: "Connected" },
                    { label: "Shopify", status: "Connected" },
                    { label: "Magento 2", status: "Ready" },
                  ].map((p) => (
                    <div key={p.label} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                      <span className="font-semibold text-brand-black">{p.label}</span>
                      <span className="text-xs font-bold text-brand-green">{p.status}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-brand-light-grey/60 flex items-center gap-2 text-xs text-brand-grey">
                  <Lock size={12} /> PCI DSS Level 1 Compliant
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-4 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
          {[
            { icon: ShieldCheck, label: "PCI DSS Compliant" },
            { icon: CreditCard, label: "All Major Cards + Wallets" },
            { icon: Zap, label: "Next-Day Settlement" },
            { icon: CheckCircle, label: "UK-Based Support" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <item.icon size={16} className="text-brand-aqua" />
              <span className="text-sm font-semibold text-brand-black">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Choose your platform */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Choose Your Platform</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Which platform does your store run on?</h2>
            <p className="text-sm text-brand-grey max-w-xl mx-auto mt-3">Select your platform to see setup details, features, and pricing information.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {PLATFORMS.map((p) => <PlatformCard key={p.slug} p={p} />)}
            <Link to="/contact?product=Custom%20API%20Integration&category=integrations&source=merchant-integrations" className="group flex items-center gap-4 bg-brand-aqua-light border border-brand-aqua/20 rounded-2xl p-5 hover:bg-brand-aqua hover:border-brand-aqua transition-all" data-testid="merchant-platform-custom-api">
              <div className="w-11 h-11 rounded-xl bg-brand-aqua flex items-center justify-center flex-shrink-0 group-hover:bg-white transition-colors">
                <Globe size={20} className="text-white group-hover:text-brand-aqua transition-colors" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-brand-aqua-dark group-hover:text-white transition-colors">Custom / API</p>
                <p className="text-xs text-brand-grey group-hover:text-white/70 transition-colors">Bespoke integration via our REST API.</p>
              </div>
              <ArrowRight size={16} className="text-brand-aqua group-hover:text-white flex-shrink-0 transition-colors" />
            </Link>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">What&apos;s Included</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Everything you need to sell online</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: CreditCard, title: "All payment methods", desc: "Visa, Mastercard, Amex, Apple Pay, Google Pay — one integration, every card." },
              { icon: ShieldCheck, title: "Built-in security", desc: "3D Secure 2.x, tokenisation, and AI-powered fraud scoring as standard." },
              { icon: Zap, title: "Fast settlement", desc: "Next working day into your UK bank account. No extra charges." },
            ].map((f) => (
              <div key={f.title} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6">
                <f.icon size={24} className="text-brand-aqua mb-4" />
                <h3 className="font-heading font-bold text-brand-black mb-2">{f.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA with form */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-6">Set up online payments for your store</h2>
            <div className="space-y-3">
              {[
                "Plug-and-play for WooCommerce, Shopify, Magento and more",
                "Lower rates than Stripe and Shopify Payments for most UK merchants",
                "Real human support — not a chatbot",
                "Order before 2pm for next-working-day go-live",
                "No lock-in contracts",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                  <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Get your integration pricing</h3>
            <p className="text-sm text-brand-grey mb-4">Tell us about your store and we&apos;ll send you a clear quote — usually within a few hours.</p>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
